import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, CheckCircle2Icon, GiftIcon } from 'lucide-react';
import { PreviewCarousel } from './PreviewCarousel';
import { bonuses, volumes } from '../data/offer';
import type { Volume } from '../types/content';
// Each volume maps to one of the existing accent tokens.
const accentStyles: Record<Volume['accent'], {
  border: string;
  text: string;
}> = {
  info: {
    border: 'border-info/30',
    text: 'text-info'
  },
  primary: {
    border: 'border-primary-soft/30',
    text: 'text-primary-soft'
  },
  highlight: {
    border: 'border-highlight/30',
    text: 'text-highlight'
  },
  danger: {
    border: 'border-danger-soft/30',
    text: 'text-danger-soft'
  }
};
export function ProductOffer() {
  return <>
      <section className="bg-surface px-4 py-16 sm:py-24" data-id="element-147" data-mp="fj57v9hrp24a">
        <div className="mx-auto max-w-5xl" data-id="element-148" data-mp="fjol4cwh32bz">
          <header className="text-center" data-id="element-149" data-mp="khyimu5o6f0r">
            <h2 className="font-display text-3xl font-bold text-highlight sm:text-5xl" data-id="element-150" data-mp="khq653dglbq3">
              🏆 PROTOCOLO CALMA APLICADA
            </h2>
            <p className="mx-auto mt-4 max-w-4xl text-lg font-medium" data-id="element-151" data-mp="3gfykwz3503bg">
              Tu sistema en 4 volúmenes para separar lo que está pasando, soltar lo que ya no mejora por seguir pensándolo y continuar con más calma y claridad.
            </p>
          </header>
          <h3 className="mt-12 text-center font-display text-2xl font-bold" data-id="element-152" data-mp="3qexkzt9u6qmu">
            📚 4 VOLÚMENES COMPLETOS
          </h3>
          <div className="mt-7 space-y-5" data-id="element-153" data-mp="2xpgzzgojqc0n">
            {volumes.map((volume, index) => {
            const accent = accentStyles[volume.accent];
            return <motion.article key={volume.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.06
            }} className={`flex gap-5 rounded-2xl border ${accent.border} bg-canvas p-5 sm:p-7`} data-id="element-154" data-mp="3bhopcndi30pb" data-mpi={index}>
                  <BookOpenIcon className={`mt-1 h-7 w-7 shrink-0 ${accent.text}`} aria-hidden="true" data-id="element-155" data-mp="hxlby732u584" />
                  <div className="flex-1" data-id="element-156" data-mp="mvv6qgkw33t7">
                    <p className={`text-xs font-bold tracking-brand ${accent.text}`} data-id="element-157" data-mp="ro653ezzehjn">
                      {volume.label}
                    </p>
                    <h4 className="mt-1 font-display text-lg font-bold sm:text-xl" data-id="element-158" data-mp="wlxzqgs0ghrj">
                      {volume.title}
                    </h4>
                    {volume.subtitle && <p className="mt-1 text-sm text-foreground-subtle sm:text-base" data-id="element-159" data-mp="3y231c2fypur">
                        {volume.subtitle}
                      </p>}
                    <ul className="mt-4 space-y-2" data-id="element-160" data-mp="8wde1q4h36ek">
                      {volume.items.map(item => <li key={item} className="flex gap-2 text-sm leading-relaxed text-foreground-muted sm:text-base" data-id="element-161" data-mp="dxj7jq5yixp5">
                          <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary-soft" aria-hidden="true" data-id="element-162" data-mp="1kexzq81eey4v" />
                          {item}
                        </li>)}
                    </ul>
                    <p className="mt-5 border-t border-foreground/10 pt-4 text-sm sm:text-base" data-id="element-163" data-mp="39w1ufnime543">
                      <span className="font-semibold text-foreground-subtle" data-id="element-164" data-mp="34wz1h1fwzpch">Resultado: </span>
                      <span className={`font-bold italic ${accent.text}`} data-id="element-165" data-mp="3022xd2xelbj0">{volume.result}</span>
                    </p>
                  </div>
                </motion.article>;
          })}
          </div>
        </div>
      </section>
      <PreviewCarousel data-id="element-166" data-mp="2m4mq5p7a9dq7" />
      <section className="bg-canvas px-4 py-16 sm:py-24" data-id="element-167" data-mp="2m44v2vwcx5jv">
        <div className="mx-auto max-w-5xl" data-id="element-168" data-mp="2m6r3exr2np35">
          <header className="text-center" data-id="element-169" data-mp="2h6cuzelk6d4l">
            <GiftIcon className="mx-auto h-10 w-10 text-highlight" aria-hidden="true" data-id="element-170" data-mp="2h7e7zom4x9oy" />
            <h2 className="mt-3 font-display text-3xl font-bold text-highlight sm:text-5xl" data-id="element-171" data-mp="2c81jkzfm7uk9">
              5 BONOS DE REGALO
            </h2>
          </header>
          <div className="mt-10 space-y-4" data-id="element-172" data-mp="aekfu6ulvdus">
            {bonuses.map(({
            number,
            title,
            items,
            value
          }) => <article key={number} className="flex flex-col gap-4 rounded-2xl border border-highlight/20 bg-surface p-5 sm:flex-row sm:items-start sm:p-7" data-id="element-173" data-mp="344z8isuqlzbc">
                <span className="w-fit whitespace-nowrap rounded-lg bg-highlight/10 px-3 py-2 font-bold text-highlight" data-id="element-174" data-mp="2u7uk0mdf5rrd">
                  🎁 BONO {number}
                </span>
                <div className="flex-1" data-id="element-175" data-mp="2kikrx6iks939">
                  <h3 className="font-bold" data-id="element-176" data-mp="2khjewwi01ciw">{title}</h3>
                  
                  <ul className="mt-3 space-y-1.5" data-id="element-177" data-mp="2pakuz7txzi42">
                    {items.map(item => <li key={item} className="flex gap-2 text-sm leading-relaxed text-foreground-muted" data-id="element-178" data-mp="2bio93liva4g4">
                        <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-highlight" aria-hidden="true" data-id="element-179" data-mp="2ggg10fjihqkb" />
                        {item}
                      </li>)}
                  </ul>
                </div>
                <div className="flex shrink-0 items-center gap-3" data-id="element-180" data-mp="26i094acnud9r">
                  <span className="text-xs font-semibold text-foreground-faint" data-id="element-181" data-mp="1mwzzrxs45pl5">VALOR:</span>
                  <span className="text-sm text-foreground-faint line-through" data-id="element-182" data-mp="1ivobbvkz7aej">
                    {value}
                  </span>
                  <span className="rounded-full bg-primary-soft px-3 py-1 text-sm font-bold text-canvas" data-id="element-183" data-mp="1wwh3warpvvf0">
                    GRATIS
                  </span>
                </div>
              </article>)}
          </div>
          <div className="mt-8 rounded-2xl border border-highlight/30 bg-highlight/10 p-6 text-center" data-id="element-184" data-mp="25ukqlaefuy8e">
            <p className="text-xs font-bold tracking-brand text-highlight" data-id="element-185" data-mp="21u0so4m1jql7">
              VALOR TOTAL BONOS
            </p>
            <p className="mt-1 text-2xl font-bold text-foreground-subtle line-through" data-id="element-186" data-mp="2fnru0ppmc45u">
              $52.000
            </p>
            <p className="mt-2 font-display text-xl font-bold text-highlight" data-id="element-187" data-mp="2pjv3kgmsv52t">
              GRATIS con tu Protocolo Calma Aplicada
            </p>
          </div>
        </div>
      </section>
    </>;
}